import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';

// URL backend helper
const getApiUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  return `http://${window.location.hostname}:8000`;
};

// Formatter angka rupiah
const fmtNum = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Badge / Pill component
const Pill = ({ children, bg = '#0284c7', color = '#fff', outline = false }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 9999,
    fontSize: '0.70rem',
    fontWeight: 700,
    background: outline ? 'transparent' : bg,
    color,
    border: outline ? `1px solid ${color}` : 'none',
    letterSpacing: '0.4px'
  }}>
    {children}
  </span>
);

const TerimaBarangApotek = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // Lokasi Apotek / Farmasi
  const [lokasi, setLokasi] = useState(() => {
    return routeLocation.state?.lokasi || localStorage.getItem('simrs_lokasi') || '00001';
  });

  const [username, setUsername] = useState('Administrator');
  const [time, setTime] = useState({ date: '', clock: '' });

  // Clock timer
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      setTime({
        date: `${dd}/${mm}/${yyyy}`,
        clock: `${hh}:${min}:${ss}`
      });
    };
    updateTime();
    const iv = setInterval(updateTime, 1000);
    return () => clearInterval(iv);
  }, []);

  // Ambil user info
  useEffect(() => {
    try {
      const u = localStorage.getItem('username') || localStorage.getItem('user');
      if (u) setUsername(u);
    } catch (_) {}
  }, []);

  // ==========================================
  // STATE HEADER TTB
  // ==========================================
  const [noPo, setNoPo] = useState('');
  const [tglPo, setTglPo] = useState('');
  const [kdSuplier, setKdSuplier] = useState('');
  const [nmSuplier, setNmSuplier] = useState('');
  const [pkp, setPkp] = useState('PKP');
  const [caraBayar, setCaraBayar] = useState('Tempo');
  const [tempo, setTempo] = useState('30');
  const [noFaktur, setNoFaktur] = useState('');
  const [tglFaktur, setTglFaktur] = useState(() => {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });

  // Nomor OPH & Nomor TTB
  const [noOph, setNoOph] = useState('');
  const [noBukti, setNoBukti] = useState('');
  const [isApproved, setIsApproved] = useState(false);

  // Available items dari SP yang dipilih
  const [spItemsList, setSpItemsList] = useState([]);

  // ==========================================
  // STATE BARIS INPUT OBAT (Aktif)
  // ==========================================
  const [inputKdObat, setInputKdObat] = useState('');
  const [inputNmObat, setInputNmObat] = useState('');
  const [inputQtyBeli, setInputQtyBeli] = useState('');
  const [inputSatuanBeli, setInputSatuanBeli] = useState('');
  const [inputPriceList, setInputPriceList] = useState(0);
  const [inputTotPriceList, setInputTotPriceList] = useState(0);
  const [inputDiscPercent, setInputDiscPercent] = useState('');
  const [inputDiscRp, setInputDiscRp] = useState(0);
  const [inputHrgJadiBeli, setInputHrgJadiBeli] = useState(0);
  const [inputPpnBeli, setInputPpnBeli] = useState(0);
  const [inputKonversi, setInputKonversi] = useState(1);
  const [inputSatuanJual, setInputSatuanJual] = useState('');
  const [inputHargaNett, setInputHargaNett] = useState(0);
  const [inputPpnNett, setInputPpnNett] = useState(0);
  const [inputHargaDasar, setInputHargaDasar] = useState(0);
  const [maxQtySp, setMaxQtySp] = useState(0);
  const [spDiscDefault, setSpDiscDefault] = useState(0);

  // Ref input
  const inputNoPoRef = useRef(null);
  const inputNoFakturRef = useRef(null);
  const inputQtyRef = useRef(null);
  const inputDiscRef = useRef(null);
  const inputKonvRef = useRef(null);

  // ==========================================
  // STATE TABEL DAFTAR ITEM DRAFT TTB
  // ==========================================
  const [items, setItems] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // ==========================================
  // STATE SUMMARY FOOTER
  // ==========================================
  const [discTunai, setDiscTunai] = useState('');
  const [materai, setMaterai] = useState('');
  const [totals, setTotals] = useState({
    totpricelist: 0,
    subtotalharga: 0,
    subtotaldiscrp: 0,
    totppnbeli: 0,
    harsat_sum: 0
  });

  // Loading & Toast
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 4000);
  };

  // ==========================================
  // MODALS
  // ==========================================
  // Modal Lookup SP
  const [showModalSp, setShowModalSp] = useState(false);
  const [spHistoryList, setSpHistoryList] = useState([]);
  const [searchSpKeyword, setSearchSpKeyword] = useState('');
  const [isLoadingSpList, setIsLoadingSpList] = useState(false);

  // Modal Lookup Obat dari SP
  const [showModalObat, setShowModalObat] = useState(false);

  // Modal Konfirmasi Approval
  const [showModalConfirmApp, setShowModalConfirmApp] = useState(false);

  // Modal Cek Fisik
  const [showModalCekFisik, setShowModalCekFisik] = useState(false);
  const [targetCekFisikItem, setTargetCekFisikItem] = useState(null);

  // Modal PDF Viewer
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  // Modal Riwayat TTB
  const [showModalHistory, setShowModalHistory] = useState(false);

  // ==========================================
  // STATE LAPORAN PEMBELIAN & RETUR (GAMBAS)
  // ==========================================
  const [showModalLaporan, setShowModalLaporan] = useState(false);
  const [lapJenis, setLapJenis] = useState('pembelian'); // 'pembelian' | 'retur'
  const [lapKdGudang, setLapKdGudang] = useState(() => routeLocation.state?.lokasi || localStorage.getItem('simrs_lokasi') || '00001');
  const [lapPeriode1, setLapPeriode1] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `01/${mm}/${yyyy}`;
  });
  const [lapPeriode2, setLapPeriode2] = useState(() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  });
  const [lapFormat, setLapFormat] = useState('rekap'); // 'rekap' | 'rinci' | 'both'
  const [lapSummary, setLapSummary] = useState(null);
  const [isLoadingLapSummary, setIsLoadingLapSummary] = useState(false);
  const [gudangList, setGudangList] = useState([]);

  // State ProgressBar Laporan (pbbar Gambas)
  const [lapProgress, setLapProgress] = useState(0);
  const [isProcessingLap, setIsProcessingLap] = useState(false);
  const [lapStatusMsg, setLapStatusMsg] = useState('');

  const [ttbHistoryList, setTtbHistoryList] = useState([]);
  const [searchTtbKeyword, setSearchTtbKeyword] = useState('');

  // ==========================================
  // HANDLER PENCARIAN & CEK SP
  // ==========================================
  const handleCekSp = async (poNumber = noPo) => {
    const cleanPo = (poNumber || '').trim().toUpperCase();
    if (!cleanPo) {
      showToast('Nomor Surat Pesanan (PO) tidak boleh kosong', 'warning');
      inputNoPoRef.current?.focus();
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/ttb_cek_sp?no_sp=${encodeURIComponent(cleanPo)}&lokasi=${lokasi}`);
      const j = await res.json();

      if (res.ok && j.status === 1) {
        const sp = j.sp;
        setNoPo(sp.no_sp);
        setTglPo(sp.tanggal || '');
        setKdSuplier(sp.kd_suplier || '');
        setNmSuplier(sp.nm_suplier || '');
        setCaraBayar(sp.cara_bayar || 'Tempo');
        setTempo(sp.tempo || '30');
        setPkp(sp.pkp || 'PKP');

        setSpItemsList(j.items || []);
        showToast(`SP ${sp.no_sp} ditemukan (${(j.items || []).length} item obat)`, 'success');

        // Pindah fokus ke nomor faktur
        setTimeout(() => inputNoFakturRef.current?.focus(), 200);
      } else {
        showToast(j.msg || 'Nomor Surat Pesanan tidak ditemukan', 'danger');
        setSpItemsList([]);
      }
    } catch (e) {
      showToast('Terjadi kesalahan saat memeriksa SP: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Buka Modal Daftar SP
  const handleOpenModalSp = async () => {
    setShowModalSp(true);
    setIsLoadingSpList(true);
    try {
      const res = await fetch(`${getApiUrl()}/ttb_list_sp?lokasi=${lokasi}&search=${encodeURIComponent(searchSpKeyword)}`);
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setSpHistoryList(j.data || []);
      }
    } catch (e) {
      showToast('Gagal mengambil daftar SP: ' + e.message, 'danger');
    } finally {
      setIsLoadingSpList(false);
    }
  };

  const handleSelectSp = (spItem) => {
    setShowModalSp(false);
    setNoPo(spItem.no_sp);
    handleCekSp(spItem.no_sp);
  };

  // ==========================================
  // HANDLER CEK FAKTUR
  // ==========================================
  const handleCekFaktur = async () => {
    const cleanFaktur = (noFaktur || '').trim().toUpperCase();
    if (!cleanFaktur) return;

    try {
      const res = await fetch(`${getApiUrl()}/ttb_cek_faktur?no_faktur=${encodeURIComponent(cleanFaktur)}&lokasi=${lokasi}`);
      const j = await res.json();
      if (res.ok && j.status === 1 && j.exists) {
        const hd = j.header;
        setNoOph(hd.no_oph || '');
        setTglFaktur(hd.tgl_faktur || tglFaktur);
        setNoPo(hd.no_po || noPo);
        setTglPo(hd.tgl_po || tglPo);
        setKdSuplier(hd.kd_suplier || kdSuplier);
        setNmSuplier(hd.nm_suplier || nmSuplier);
        setCaraBayar(hd.cara_bayar || caraBayar);
        setTempo(hd.tempo || tempo);
        setDiscTunai(hd.disccash ? String(hd.disccash) : '');
        setMaterai(hd.materai ? String(hd.materai) : '');

        if (j.is_approved) {
          setIsApproved(true);
          setNoBukti(hd.no_bukti || '');
          showToast(`Nomor Faktur sudah pernah diapprove (No TTB: ${hd.no_bukti}). Data hanya dapat dicetak ulang.`, 'info');
        } else {
          setIsApproved(false);
          setNoBukti('');
          showToast('Draft TTB untuk faktur ini ditemukan dan dimuat kembali.', 'info');
        }

        // Muat items
        loadItemsByOph(hd.no_oph);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ==========================================
  // LOAD ITEMS DTINAPOTEK
  // ==========================================
  const loadItemsByOph = useCallback(async (oph = noOph) => {
    if (!oph) return;
    try {
      const res = await fetch(`${getApiUrl()}/ttb_list_items?no_oph=${encodeURIComponent(oph)}&lokasi=${lokasi}`);
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setItems(j.items || []);
        if (j.totals) {
          setTotals(j.totals);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [lokasi, noOph]);

  // ==========================================
  // KALKULASI REALTIME SAAT INPUT DATA OBAT
  // ==========================================
  const handleSelectObatFromSp = (ob) => {
    setInputKdObat(ob.kd_obat);
    setInputNmObat(ob.nm_obat);
    setInputSatuanBeli(ob.satuanbeli || 'Box');
    setInputSatuanJual(ob.satuanjual || 'Pcs');
    setInputPriceList(ob.pricelist || ob.harsat || 0);
    setInputKonversi(ob.konversi || 1);
    setMaxQtySp(ob.qty_sp || 0);
    setSpDiscDefault(ob.disc || 0);

    // Default qty: 1 atau sesuai sisa SP
    const defaultQty = ob.qty_sp > 0 ? ob.qty_sp : 1;
    setInputQtyBeli(String(defaultQty));
    setInputDiscPercent(String(ob.disc || 0));

    // Kalkulasi
    calculateItemFields(defaultQty, ob.pricelist || ob.harsat || 0, ob.disc || 0, ob.konversi || 1);

    setShowModalObat(false);
    setTimeout(() => inputQtyRef.current?.focus(), 150);
  };

  const calculateItemFields = (qtyVal, priceVal, discVal, konvVal) => {
    const q = parseFloat(qtyVal) || 0;
    const p = parseFloat(priceVal) || 0;
    const d = parseFloat(discVal) || 0;
    const k = parseFloat(konvVal) || 1;

    const totPrice = q * p;
    const discRp = (totPrice * d) / 100;
    const hrgJadi = totPrice - discRp;
    const ppnBeli = pkp === 'PKP' ? Math.round((hrgJadi * 11) / 100) : 0;

    const totQtyRetail = q * k;
    const hrgDasar = totQtyRetail > 0 ? Math.round(hrgJadi / totQtyRetail) : 0;

    setInputTotPriceList(totPrice);
    setInputDiscRp(discRp);
    setInputHrgJadiBeli(hrgJadi);
    setInputPpnBeli(ppnBeli);

    // Penjualan preview
    setInputHargaNett(hrgJadi);
    setInputPpnNett(ppnBeli);
    setInputHargaDasar(hrgDasar);
  };

  const handleQtyChange = (val) => {
    setInputQtyBeli(val);
    const q = parseFloat(val) || 0;
    if (maxQtySp > 0 && q > maxQtySp) {
      showToast(`Perhatian: Jumlah diterima (${q}) melebihi pesanan SP (${maxQtySp})`, 'warning');
    }
    calculateItemFields(val, inputPriceList, inputDiscPercent, inputKonversi);
  };

  const handleDiscChange = (val) => {
    setInputDiscPercent(val);
    const d = parseFloat(val) || 0;
    if (spDiscDefault > 0 && d < spDiscDefault) {
      showToast(`Perhatian: Diskon (${d}%) lebih kecil dari diskon SP (${spDiscDefault}%)`, 'warning');
    }
    calculateItemFields(inputQtyBeli, inputPriceList, val, inputKonversi);
  };

  const handleKonvChange = (val) => {
    setInputKonversi(val);
    calculateItemFields(inputQtyBeli, inputPriceList, inputDiscPercent, val);
  };

  // Bersihkan Baris Input
  const handleBersihInputRow = () => {
    setInputKdObat('');
    setInputNmObat('');
    setInputQtyBeli('');
    setInputSatuanBeli('');
    setInputPriceList(0);
    setInputTotPriceList(0);
    setInputDiscPercent('');
    setInputDiscRp(0);
    setInputHrgJadiBeli(0);
    setInputPpnBeli(0);
    setInputKonversi(1);
    setInputSatuanJual('');
    setInputHargaNett(0);
    setInputPpnNett(0);
    setInputHargaDasar(0);
    setMaxQtySp(0);
    setSpDiscDefault(0);
    setSelectedRowIndex(null);
  };

  // ==========================================
  // SIMPAN SATU ITEM KE DRAFT (DTINAPOTEK)
  // ==========================================
  const handleSimpanItem = async () => {
    if (!inputKdObat) {
      showToast('Pilih obat terlebih dahulu', 'warning');
      return;
    }
    const q = parseFloat(inputQtyBeli) || 0;
    if (q <= 0) {
      showToast('Qty Beli harus lebih besar dari 0', 'warning');
      inputQtyRef.current?.focus();
      return;
    }
    if (!noFaktur) {
      showToast('Nomor Faktur Pemasok harus diisi', 'warning');
      inputNoFakturRef.current?.focus();
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        lokasi,
        no_oph: noOph,
        no_po: noPo,
        tgl_po: tglPo,
        kd_suplier: kdSuplier,
        nm_suplier: nmSuplier,
        tempo,
        cara_bayar: caraBayar,
        no_faktur: noFaktur.trim().toUpperCase(),
        tgl_faktur: tglFaktur,
        user_id: username,

        kd_obat: inputKdObat,
        nm_obat: inputNmObat,
        qtybeli: q,
        kdsatuanbeli: '',
        nmsatuanbeli: inputSatuanBeli,
        totpricelist: inputTotPriceList,
        disc: parseFloat(inputDiscPercent) || 0,
        discrp: inputDiscRp,
        hrgjadibeli: inputHrgJadiBeli,
        ppnbeli: inputPpnBeli,
        konversi: parseFloat(inputKonversi) || 1,
        kdsatuanjual: '',
        nmsatuanjual: inputSatuanJual,
        harga: inputHargaNett,
        ppn: inputPpnNett,
        harsat: inputHargaDasar,
        qtypesan: maxQtySp
      };

      const res = await fetch(`${getApiUrl()}/ttb_simpan_item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json();

      if (res.ok && j.status === 1) {
        showToast(j.msg || 'Obat berhasil disimpan ke draft', 'success');
        setNoOph(j.no_oph);
        setItems(j.items || []);
        if (j.totals) setTotals(j.totals);
        handleBersihInputRow();
      } else {
        showToast(j.msg || 'Gagal menyimpan item ke draft', 'danger');
      }
    } catch (e) {
      showToast('Kesalahan simpan item: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Hapus item dari draft
  const handleHapusItem = async (kdObatTarget) => {
    const target = kdObatTarget || inputKdObat;
    if (!target) {
      showToast('Pilih obat yang akan dihapus dari daftar', 'warning');
      return;
    }
    if (!noOph) {
      handleBersihInputRow();
      return;
    }

    if (!window.confirm('Apakah Anda yakin ingin menghapus obat ini dari draft TTB?')) {
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/ttb_hapus_item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lokasi, no_oph: noOph, kd_obat: target })
      });
      const j = await res.json();

      if (res.ok && j.status === 1) {
        showToast(j.msg || 'Obat dihapus dari draft', 'info');
        setItems(j.items || []);
        if (j.totals) setTotals(j.totals);
        handleBersihInputRow();
      } else {
        showToast(j.msg || 'Gagal menghapus item', 'danger');
      }
    } catch (e) {
      showToast('Kesalahan hapus item: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Pilih baris tabel untuk diedit
  const handleRowClick = (item, idx) => {
    setSelectedRowIndex(idx);
    setInputKdObat(item.kd_obat);
    setInputNmObat(item.nm_obat);
    setInputQtyBeli(String(item.qtybeli || 0));
    setInputSatuanBeli(item.nmsatuanbeli || '');
    setInputPriceList((item.totpricelist && item.qtybeli) ? Math.round(item.totpricelist / item.qtybeli) : 0);
    setInputTotPriceList(item.totpricelist || 0);
    setInputDiscPercent(String(item.disc || 0));
    setInputDiscRp(item.discrp || 0);
    setInputHrgJadiBeli(item.hrgjadibeli || 0);
    setInputPpnBeli(item.ppnbeli || 0);
    setInputKonversi(item.konversi || 1);
    setInputSatuanJual(item.nmsatuanjual || '');
    setInputHargaNett(item.harga || 0);
    setInputPpnNett(item.ppn || 0);
    setInputHargaDasar(item.harsat || 0);
    setMaxQtySp(item.qtypesan || 0);
  };

  // ==========================================
  // CEK FISIK BARANG
  // ==========================================
  const handleOpenCekFisik = (item) => {
    setTargetCekFisikItem(item);
    setShowModalCekFisik(true);
  };

  const handleExecuteCekFisik = async (isApprovedFisik) => {
    if (!targetCekFisikItem || !noOph) return;
    const statusVal = isApprovedFisik ? 'V' : 'X';

    try {
      const res = await fetch(`${getApiUrl()}/ttb_update_cekfisik`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          no_oph: noOph,
          kd_obat: targetCekFisikItem.kd_obat,
          cekfisik: statusVal
        })
      });
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setItems(j.items || []);
        showToast(`Cek fisik ${targetCekFisikItem.nm_obat}: ${isApprovedFisik ? 'SESUAI (V)' : 'TIDAK SESUAI (X)'}`, isApprovedFisik ? 'success' : 'warning');
      }
    } catch (e) {
      showToast('Gagal update cek fisik: ' + e.message, 'danger');
    } finally {
      setShowModalCekFisik(false);
      setTargetCekFisikItem(null);
    }
  };

  // ==========================================
  // HITUNG TOTAL AKHIR
  // ==========================================
  const subTotalHarga = totals.subtotalharga || 0;
  const totPpnBeli = totals.totppnbeli || 0;
  const valDiscTunai = parseFloat(discTunai) || 0;
  const valMaterai = parseFloat(materai) || 0;
  const grandTotal = Math.max(0, subTotalHarga + totPpnBeli + valMaterai - valDiscTunai);

  // ==========================================
  // SIMPAN DRAFT (Proses Tombol Biru Kiri)
  // ==========================================
  const handleSimpanDraftOnly = async () => {
    if (!noOph) {
      showToast('Belum ada data barang di draft untuk disimpan', 'warning');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/ttb_simpan_draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          no_oph: noOph,
          disccash: valDiscTunai,
          materai: valMaterai,
          sub_total: subTotalHarga,
          disc: totals.subtotaldiscrp,
          discrp: totals.subtotaldiscrp,
          ppn: totPpnBeli,
          total: grandTotal
        })
      });
      const j = await res.json();
      if (res.ok && j.status === 1) {
        showToast('Draft TTB berhasil diperbarui', 'success');
      } else {
        showToast(j.msg || 'Gagal menyimpan draft', 'danger');
      }
    } catch (e) {
      showToast('Gagal simpan draft: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // PROSES APPROVAL TTB (Finalisasi & Mutasi Stok)
  // ==========================================
  const handleOpenConfirmApproval = () => {
    if (items.length === 0) {
      showToast('Tidak ada obat di daftar TTB untuk diapprove', 'warning');
      return;
    }

    // Cek apakah ada obat yang belum dicek fisik
    const unverified = items.filter(x => x.cekfisik !== 'V');
    if (unverified.length > 0) {
      showToast(`Masih ada ${unverified.length} obat yang belum diperiksa fisiknya. Klik tombol Cek Fisik pada tiap baris terlebih dahulu.`, 'warning');
      return;
    }

    setShowModalConfirmApp(true);
  };

  const handleExecuteApproval = async () => {
    setShowModalConfirmApp(false);
    setIsLoading(true);

    try {
      const res = await fetch(`${getApiUrl()}/ttb_proses_approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          no_oph: noOph,
          disccash: valDiscTunai,
          materai: valMaterai,
          user_id: username
        })
      });
      const j = await res.json();

      if (res.ok && j.status === 1) {
        setNoBukti(j.no_bukti);
        setIsApproved(true);
        showToast(j.msg || `TTB Berhasil Disetujui! No TTB: ${j.no_bukti}`, 'success');

        // Buka dialog Cetak PDF
        handleCetakPdf(j.no_bukti);
      } else {
        showToast(j.msg || 'Gagal memproses approval TTB', 'danger');
      }
    } catch (e) {
      showToast('Kesalahan saat approval: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // CETAK PDF
  // ==========================================
  const handleCetakPdf = (nomorBukti = noBukti, ophNumber = noOph) => {
    const targetBukti = nomorBukti || noBukti;
    const targetOph = ophNumber || noOph;

    if (!targetBukti && !targetOph) {
      showToast('Tidak ada data TTB untuk dicetak', 'warning');
      return;
    }

    let url = `${getApiUrl()}/ttb_cetak_pdf?lokasi=${lokasi}`;
    if (targetBukti) {
      url += `&no_bukti=${encodeURIComponent(targetBukti)}`;
      setPdfTitle(`Tanda Terima Barang: ${targetBukti}`);
    } else {
      url += `&no_oph=${encodeURIComponent(targetOph)}`;
      setPdfTitle(`Draft TTB: ${targetOph}`);
    }

    setPdfUrl(url);
    setShowModalPdf(true);
  };

  // Buka Modal Riwayat TTB
  const handleOpenModalHistory = async () => {
    setShowModalHistory(true);
    setIsLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/ttb_list_history?lokasi=${lokasi}&search=${encodeURIComponent(searchTtbKeyword)}`);
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setTtbHistoryList(j.data || []);
      }
    } catch (e) {
      showToast('Gagal memuat riwayat: ' + e.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Bersihkan Form Sepenuhnya
  const handleResetForm = () => {
    if (items.length > 0 && !isApproved) {
      if (!window.confirm('Bersihkan form tanda terima barang? Data yang belum diapprove tetap tersimpan sebagai draft.')) {
        return;
      }
    }
    setNoPo('');
    setTglPo('');
    setKdSuplier('');
    setNmSuplier('');
    setCaraBayar('Tempo');
    setTempo('30');
    setNoFaktur('');
    setNoOph('');
    setNoBukti('');
    setIsApproved(false);
    setSpItemsList([]);
    setItems([]);
    setTotals({ totpricelist: 0, subtotalharga: 0, subtotaldiscrp: 0, totppnbeli: 0, harsat_sum: 0 });
    setDiscTunai('');
    setMaterai('');
    handleBersihInputRow();
    inputNoPoRef.current?.focus();
  };

  // ==========================================
  // HANDLERS LAPORAN PEMBELIAN & RETUR
  // ==========================================
  // Load daftar gudang saat modal laporan dibuka
  useEffect(() => {
    if (showModalLaporan) {
      fetch(`${getApiUrl()}/ttb_list_gudang`)
        .then(r => r.json())
        .then(res => {
          if (res.status === 1 && res.data) {
            setGudangList(res.data);
          }
        })
        .catch(err => console.error('Gagal memuat daftar gudang:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalLaporan]);

  // Load summary saat parameter laporan berubah
  useEffect(() => {
    if (!showModalLaporan) return;
    if (!lapPeriode1 || !lapPeriode2 || lapPeriode1.length !== 10 || lapPeriode2.length !== 10) return;

    setIsLoadingLapSummary(true);
    fetch(`${getApiUrl()}/ttb_laporan_summary?jenis=${lapJenis}&lokasi=${lapKdGudang}&p1=${encodeURIComponent(lapPeriode1)}&p2=${encodeURIComponent(lapPeriode2)}`)
      .then(r => r.json())
      .then(res => {
        setIsLoadingLapSummary(false);
        if (res.status === 1) {
          setLapSummary(res);
        } else {
          setLapSummary(null);
        }
      })
      .catch(err => {
        setIsLoadingLapSummary(false);
        console.error('Gagal memuat summary laporan:', err);
      });
  }, [showModalLaporan, lapJenis, lapKdGudang, lapPeriode1, lapPeriode2]);

  // Helper format tanggal DD/MM/YYYY <-> YYYY-MM-DD untuk input date
  const dmyToYmd = (dmy) => {
    if (!dmy) return '';
    const parts = dmy.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    return '';
  };

  const ymdToDmy = (ymd) => {
    if (!ymd) return '';
    const parts = ymd.split('-');
    if (parts.length === 3) {
      return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
    return '';
  };

  // Quick Presets Periode
  const setPresetToday = () => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = now.getFullYear();
    const todayStr = `${d}/${m}/${y}`;
    setLapPeriode1(todayStr);
    setLapPeriode2(todayStr);
  };

  const setPresetThisMonth = () => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = now.getFullYear();
    setLapPeriode1(`01/${m}/${y}`);
    setLapPeriode2(`${d}/${m}/${y}`);
  };

  const setPresetLastMonth = () => {
    const now = new Date();
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const m = String(prevMonthDate.getMonth() + 1).padStart(2, '0');
    const y = prevMonthDate.getFullYear();
    const endD = String(lastDayPrevMonth.getDate()).padStart(2, '0');
    setLapPeriode1(`01/${m}/${y}`);
    setLapPeriode2(`${endD}/${m}/${y}`);
  };

  // Handle proses laporan / cetak PDF dengan ProgressBar (pbbar Gambas)
  const handleProsesLaporan = async () => {
    if (!lapPeriode1 || !lapPeriode2) {
      showToast('Cek Isian periode masih ada yg salah !', 'danger');
      return;
    }

    const p1Parts = lapPeriode1.split('/');
    const p2Parts = lapPeriode2.split('/');
    if (p1Parts.length !== 3 || p2Parts.length !== 3) {
      showToast('Format periode tanggal harus DD/MM/YYYY', 'danger');
      return;
    }

    const d1 = new Date(parseInt(p1Parts[2], 10), parseInt(p1Parts[1], 10) - 1, parseInt(p1Parts[0], 10));
    const d2 = new Date(parseInt(p2Parts[2], 10), parseInt(p2Parts[1], 10) - 1, parseInt(p2Parts[0], 10));

    if (d2 < d1) {
      showToast('Cek Isian periode masih ada yg salah ! Periode akhir lebih kecil dari periode awal.', 'danger');
      return;
    }

    const base = getApiUrl();
    const rekapUrl = `${base}/ttb_laporan_pembelian_pdf?jenis=${lapJenis}&tipe=rekap&lokasi=${lapKdGudang}&p1=${encodeURIComponent(lapPeriode1)}&p2=${encodeURIComponent(lapPeriode2)}`;
    const rinciUrl = `${base}/ttb_laporan_pembelian_pdf?jenis=${lapJenis}&tipe=rinci&lokasi=${lapKdGudang}&p1=${encodeURIComponent(lapPeriode1)}&p2=${encodeURIComponent(lapPeriode2)}`;

    const targetUrl = lapFormat === 'rinci' ? rinciUrl : rekapUrl;
    const reportTitle = lapJenis === 'retur'
      ? (lapFormat === 'rinci' ? 'LAPORAN RINCIAN RETUR PEMBELIAN PER NAMA OBAT' : 'LAPORAN REKAPITULASI RETUR PEMBELIAN')
      : (lapFormat === 'rinci' ? 'LAPORAN RINCIAN PEMBELIAN PER NAMA OBAT' : 'LAPORAN REKAPITULASI PEMBELIAN (TTB)');

    setIsProcessingLap(true);
    setLapProgress(10);
    setLapStatusMsg('Menghubungkan ke basis data apotek & memuat transaksi...');

    const progInterval = setInterval(() => {
      setLapProgress(prev => {
        if (prev < 30) {
          setLapStatusMsg(`Memfilter transaksi periode ${lapPeriode1} s.d ${lapPeriode2}...`);
          return prev + 10;
        } else if (prev < 65) {
          setLapStatusMsg('Menghitung subtotal, discount, PPN & rekapitulasi data...');
          return prev + 15;
        } else if (prev < 90) {
          setLapStatusMsg('Menyusun tata letak PDF resmi RS Mary Cileungsi Hijau...');
          return prev + 10;
        }
        return prev;
      });
    }, 250);

    try {
      const res = await fetch(targetUrl);
      clearInterval(progInterval);

      if (!res.ok) {
        throw new Error('Gagal memuat dokumen PDF laporan dari server');
      }

      setLapProgress(100);
      setLapStatusMsg('Proses Laporan selesai, silahkan periksa dokumen laporan.');

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      setTimeout(() => {
        setPdfUrl(blobUrl);
        setPdfTitle(reportTitle);
        setShowModalPdf(true);
        setIsProcessingLap(false);

        if (lapFormat === 'both') {
          window.open(rinciUrl, '_blank');
        }
      }, 500);
    } catch (err) {
      clearInterval(progInterval);
      setIsProcessingLap(false);
      setLapProgress(0);
      setLapStatusMsg('Gagal: ' + err.message);
      showToast('Gagal memproses laporan: ' + err.message, 'danger');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif"
    }}>

      {/* TOAST NOTIFIKASI */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          background: toast.variant === 'danger' ? '#b91c1c' : toast.variant === 'warning' ? '#d97706' : toast.variant === 'info' ? '#0284c7' : '#059669',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontWeight: 600,
          fontSize: '0.86rem'
        }}>
          <i className={`bi ${toast.variant === 'danger' ? 'bi-exclamation-octagon' : toast.variant === 'warning' ? 'bi-exclamation-triangle' : toast.variant === 'info' ? 'bi-info-circle' : 'bi-check-circle'}`} />
          {toast.message}
        </div>
      )}

      {/* 1. TOP HOSPITAL BAR (IDENTITAS RESMI RS MARY CILEUNGSI HIJAU) */}
      <header style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#fff',
        padding: '6px 16px',
        borderBottom: '2px solid #0284c7',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/logo.png"
            alt="Logo RS"
            style={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              background: '#fff',
              borderRadius: 6,
              padding: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 800, fontSize: '0.96rem', letterSpacing: '0.5px' }}>
                TANDA TERIMA BARANG APOTEK
              </span>
              <Pill bg="rgba(14,165,233,0.3)" color="#38bdf8" outline>
                {lokasi === '00001' ? 'Apotek (00001)' : 'Farmasi (00002)'}
              </Pill>
              {noBukti && (
                <Pill bg="#059669" color="#fff">
                  TTB: {noBukti}
                </Pill>
              )}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.2 }}>
              <span style={{ color: '#e2e8f0', fontWeight: 700 }}>RS MARY CILEUNGSI HIJAU</span> &bull; Jl. Raya Narogong KM 21 Cileungsi Bogor &bull; Telp. (021) 8249-2222 Fax. (021) 8249-3902
            </div>
          </div>
        </div>

        {/* Shortcuts & Lokasi Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => {
              setLapKdGudang(lokasi || '00001');
              setShowModalLaporan(true);
            }}
            className="btn btn-sm d-flex align-items-center gap-1.5"
            style={{
              fontSize: '0.74rem',
              padding: '4px 10px',
              borderRadius: 5,
              background: '#0284c7',
              color: '#fff',
              border: '1px solid #0369a1',
              fontWeight: 700,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
            title="Buka Menu Laporan Pembelian & Retur (Gambas)"
          >
            <i className="bi bi-file-earmark-bar-graph-fill" /> Laporan Pembelian
          </button>

          <button
            onClick={handleOpenModalHistory}
            className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: 5 }}
            title="Buka Riwayat TTB Selesai"
          >
            <i className="bi bi-clock-history" /> Riwayat TTB
          </button>

          <button
            onClick={() => navigate('/SuratPesananApotek')}
            className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: 5 }}
            title="Ke Surat Pesanan Apotek"
          >
            <i className="bi bi-cart3" /> Surat Pesanan
          </button>

          {/* Lokasi Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: 6 }}>
            <span style={{ fontSize: '0.70rem', color: '#cbd5e1' }}>Lokasi:</span>
            <select
              value={lokasi}
              onChange={(e) => {
                setLokasi(e.target.value);
                localStorage.setItem('simrs_lokasi', e.target.value);
                handleResetForm();
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#38bdf8',
                fontSize: '0.74rem',
                fontWeight: 700,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="00001" style={{ color: '#0f172a' }}>Apotek (00001)</option>
              <option value="00002" style={{ color: '#0f172a' }}>Farmasi (00002)</option>
            </select>
          </div>

          {/* User badge */}
          <div style={{ fontSize: '0.72rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 4 }}>
            <i className="bi bi-person-circle text-info" /> {username}
          </div>

          {/* Home Nav */}
          <button
            onClick={() => navigate('/home')}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 5,
              color: '#fff',
              width: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            title="Kembali ke Dashboard Utama"
          >
            <i className="bi bi-house-door-fill" style={{ fontSize: '0.75rem' }} />
          </button>
        </div>
      </header>

      {/* 2. WINDOW TITLE BAR WITH DIGITAL CLOCK (MENYERUPAI GAMBAS SCREENSHOT) */}
      <div style={{
        background: '#041b3a',
        borderBottom: '2px solid #0284c7',
        padding: '4px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#fff'
      }}>
        <div style={{ fontWeight: 800, fontSize: '0.92rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
          TANDA TERIMA BARANG {noOph ? <span style={{ color: '#38bdf8', fontSize: '0.80rem' }}>(No. OPH: {noOph})</span> : null}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <div style={{
            background: '#000',
            color: '#10b981',
            fontFamily: 'monospace',
            fontWeight: 800,
            fontSize: '0.82rem',
            padding: '2px 8px',
            border: '1px solid #1e3a8a',
            borderRadius: 2
          }}>
            {time.date}
          </div>
          <div style={{
            background: '#000',
            color: '#10b981',
            fontFamily: 'monospace',
            fontWeight: 800,
            fontSize: '0.82rem',
            padding: '2px 8px',
            border: '1px solid #1e3a8a',
            borderRadius: 2
          }}>
            {time.clock}
          </div>
        </div>
      </div>

      {/* 3. PANEL INPUT HEADER (SURAT PESANAN, PEMASOK, PAJAK, PEMBAYARAN, FAKTUR) */}
      <div style={{
        background: '#cbd5e1',
        padding: '6px 8px',
        display: 'grid',
        gridTemplateColumns: '1.4fr 2fr 0.6fr 1.2fr 1.8fr',
        gap: 6,
        borderBottom: '2px solid #94a3b8'
      }}>

        {/* BOX 1: SURAT PESANAN */}
        <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', background: '#e2e8f0' }}>
          <div style={{ background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px' }}>
            SURAT PESANAN
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4 }}>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>NOMOR</div>
              <div style={{ display: 'flex' }}>
                <input
                  ref={inputNoPoRef}
                  type="text"
                  value={noPo}
                  onChange={(e) => setNoPo(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCekSp();
                  }}
                  placeholder="Ketik / Cari SP"
                  style={{
                    width: '100%',
                    background: '#fef08a',
                    border: '1px solid #ca8a04',
                    padding: '2px 4px',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: '#000'
                  }}
                />
                <button
                  type="button"
                  onClick={handleOpenModalSp}
                  title="Pilih SP dari daftar"
                  style={{
                    background: '#0284c7',
                    border: 'none',
                    color: '#fff',
                    padding: '0 6px',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bi bi-search" style={{ fontSize: '0.68rem' }} />
                </button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>TANGGAL</div>
              <input
                type="text"
                readOnly
                value={tglPo}
                placeholder="dd/mm/yyyy"
                style={{
                  width: '100%',
                  background: '#f1f5f9',
                  border: '1px solid #94a3b8',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  color: '#334155'
                }}
              />
            </div>
          </div>
        </div>

        {/* BOX 2: PEMASOK */}
        <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', background: '#e2e8f0' }}>
          <div style={{ background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px' }}>
            PEMASOK
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 4, padding: 4 }}>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>KODE</div>
              <input
                type="text"
                readOnly
                value={kdSuplier}
                style={{
                  width: '100%',
                  background: '#f1f5f9',
                  border: '1px solid #94a3b8',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  textAlign: 'center'
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b' }}>NAMA PEMASOK</div>
              <input
                type="text"
                readOnly
                value={nmSuplier}
                placeholder="Nama Pemasok dari SP"
                style={{
                  width: '100%',
                  background: '#f8fafc',
                  border: '1px solid #94a3b8',
                  padding: '2px 6px',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: '#0f172a'
                }}
              />
            </div>
          </div>
        </div>

        {/* BOX 3: PAJAK */}
        <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', background: '#e2e8f0' }}>
          <div style={{ background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px' }}>
            PAJAK
          </div>
          <div style={{ padding: 4 }}>
            <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>STATUS</div>
            <select
              value={pkp}
              onChange={(e) => {
                setPkp(e.target.value);
                calculateItemFields(inputQtyBeli, inputPriceList, inputDiscPercent, inputKonversi);
              }}
              style={{
                width: '100%',
                background: pkp === 'PKP' ? '#bbf7d0' : '#fee2e2',
                color: pkp === 'PKP' ? '#14532d' : '#991b1b',
                border: '1px solid #94a3b8',
                padding: '2px 4px',
                fontSize: '0.74rem',
                fontWeight: 800,
                textAlign: 'center'
              }}
            >
              <option value="PKP">PKP (11%)</option>
              <option value="Non">Non PKP (0%)</option>
            </select>
          </div>
        </div>

        {/* BOX 4: PEMBAYARAN */}
        <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', background: '#e2e8f0' }}>
          <div style={{ background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px' }}>
            PEMBAYARAN
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 4, padding: 4 }}>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>CARA</div>
              <input
                type="text"
                value={caraBayar}
                onChange={(e) => setCaraBayar(e.target.value)}
                style={{
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  textAlign: 'center'
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>TEMPO</div>
              <input
                type="number"
                value={tempo}
                onChange={(e) => setTempo(e.target.value)}
                style={{
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  textAlign: 'center'
                }}
              />
            </div>
          </div>
        </div>

        {/* BOX 5: FAKTUR PENJUALAN PEMASOK */}
        <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', background: '#e2e8f0' }}>
          <div style={{ background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px' }}>
            FAKTUR PENJUALAN PEMASOK
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 4, padding: 4 }}>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>NOMOR</div>
              <input
                ref={inputNoFakturRef}
                type="text"
                value={noFaktur}
                onChange={(e) => setNoFaktur(e.target.value.toUpperCase())}
                onBlur={handleCekFaktur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCekFaktur();
                }}
                placeholder="No. Faktur Pemasok"
                style={{
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #0284c7',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: '#0f172a'
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>TANGGAL</div>
              <input
                type="text"
                value={tglFaktur}
                onChange={(e) => setTglFaktur(e.target.value)}
                placeholder="dd/mm/yyyy"
                style={{
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  padding: '2px 4px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  textAlign: 'center'
                }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* 4. MULTI-LEVEL COLORED TABLE HEADER & INPUT ROW (PERSIS GAMBAS SCREENSHOT) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', flex: 1 }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.74rem',
            textAlign: 'left'
          }}>
            {/* ROW 1 HEADERS: GROUPINGS */}
            <thead>
              <tr style={{ color: '#fff', fontWeight: 800, textAlign: 'center', fontSize: '0.72rem' }}>
                <th
                  rowSpan={2}
                  style={{
                    background: '#0f766e',
                    border: '1px solid #115e59',
                    padding: '6px 8px',
                    width: '24%',
                    verticalAlign: 'middle'
                  }}
                >
                  NAMA OBAT - AKSP
                </th>
                <th
                  colSpan={6}
                  style={{
                    background: '#0d9488',
                    border: '1px solid #115e59',
                    padding: '3px 6px'
                  }}
                >
                  PEMBELIAN
                </th>
                <th
                  rowSpan={2}
                  style={{
                    background: '#ea580c',
                    border: '1px solid #c2410c',
                    padding: '6px 4px',
                    width: '60px',
                    verticalAlign: 'middle',
                    lineHeight: 1.1
                  }}
                >
                  KONV.<br />RUBAH
                </th>
                <th
                  colSpan={4}
                  style={{
                    background: '#1d4ed8',
                    border: '1px solid #1e40af',
                    padding: '3px 6px'
                  }}
                >
                  PENJUALAN
                </th>
                <th
                  rowSpan={2}
                  style={{
                    background: '#334155',
                    border: '1px solid #1e293b',
                    padding: '6px 6px',
                    width: '80px',
                    verticalAlign: 'middle'
                  }}
                >
                  CEK FISIK
                </th>
                <th
                  rowSpan={2}
                  style={{
                    background: '#475569',
                    border: '1px solid #1e293b',
                    padding: '6px 4px',
                    width: '45px',
                    verticalAlign: 'middle'
                  }}
                >
                  AKSI
                </th>
              </tr>

              {/* ROW 2 HEADERS: SUB-COLUMNS */}
              <tr style={{ color: '#fff', fontWeight: 800, textAlign: 'center', fontSize: '0.68rem' }}>
                <th style={{ background: '#0891b2', border: '1px solid #0e7490', padding: '3px 4px', width: '48px' }}>
                  QTY
                </th>
                <th style={{ background: '#ea580c', border: '1px solid #c2410c', padding: '3px 6px', width: '80px' }}>
                  SATUAN BELI
                </th>
                <th style={{ background: '#0284c7', border: '1px solid #0369a1', padding: '3px 6px', width: '90px' }}>
                  TOTAL PRICE LIST
                </th>
                <th style={{ background: '#2563eb', border: '1px solid #1d4ed8', padding: '2px 4px', width: '40px' }}>
                  DISC %
                </th>
                <th style={{ background: '#2563eb', border: '1px solid #1d4ed8', padding: '2px 4px', width: '75px' }}>
                  DISC (Rp)
                </th>
                <th style={{ background: '#0f766e', border: '1px solid #115e59', padding: '3px 6px', width: '90px' }}>
                  JUMLAH HARGA JADI
                </th>
                <th style={{ background: '#0f766e', border: '1px solid #115e59', padding: '3px 6px', width: '75px' }}>
                  PPN MASUKAN
                </th>

                {/* PENJUALAN SUB-COLUMNS */}
                <th style={{ background: '#16a34a', border: '1px solid #15803d', padding: '3px 6px', width: '80px' }}>
                  SATUAN JUAL
                </th>
                <th style={{ background: '#0f766e', border: '1px solid #115e59', padding: '3px 6px', width: '85px' }}>
                  HARGA NETT
                </th>
                <th style={{ background: '#0f766e', border: '1px solid #115e59', padding: '3px 6px', width: '70px' }}>
                  PPN [Rp]
                </th>
                <th style={{ background: '#0f766e', border: '1px solid #115e59', padding: '3px 6px', width: '85px' }}>
                  HARGA DASAR
                </th>
              </tr>

              {/* ROW 3: BARIS INPUT DATA OBAT AKTIF */}
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #0284c7' }}>
                {/* NAMA OBAT */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1' }}>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <input
                      type="text"
                      readOnly
                      value={inputNmObat}
                      placeholder={spItemsList.length > 0 ? "Pilih Obat dari SP..." : "Masukkan SP dulu..."}
                      onClick={() => {
                        if (spItemsList.length > 0) setShowModalObat(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '3px 6px',
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        border: '1px solid #0284c7',
                        background: inputNmObat ? '#f0fdf4' : '#fff',
                        cursor: spItemsList.length > 0 ? 'pointer' : 'default'
                      }}
                    />
                    <button
                      type="button"
                      disabled={spItemsList.length === 0}
                      onClick={() => setShowModalObat(true)}
                      style={{
                        background: '#0284c7',
                        border: 'none',
                        color: '#fff',
                        padding: '0 6px',
                        cursor: 'pointer',
                        borderRadius: 2
                      }}
                      title="Daftar Obat SP"
                    >
                      <i className="bi bi-list-ul" style={{ fontSize: '0.72rem' }} />
                    </button>
                  </div>
                </td>

                {/* QTY BELI */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1' }}>
                  <input
                    ref={inputQtyRef}
                    type="number"
                    value={inputQtyBeli}
                    onChange={(e) => handleQtyChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') inputDiscRef.current?.focus();
                    }}
                    placeholder="0"
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.74rem',
                      fontWeight: 800,
                      textAlign: 'center',
                      border: '1px solid #0891b2',
                      background: '#fff'
                    }}
                  />
                </td>

                {/* SATUAN BELI */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1' }}>
                  <input
                    type="text"
                    readOnly
                    value={inputSatuanBeli}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      background: '#fff7ed',
                      border: '1px solid #fdba74'
                    }}
                  />
                </td>

                {/* TOTAL PRICE LIST */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputTotPriceList)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textAlign: 'right',
                      background: '#f0f9ff',
                      border: '1px solid #bae6fd'
                    }}
                  />
                </td>

                {/* DISC % */}
                <td style={{ padding: '3px 2px', border: '1px solid #cbd5e1' }}>
                  <input
                    ref={inputDiscRef}
                    type="number"
                    value={inputDiscPercent}
                    onChange={(e) => handleDiscChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') inputKonvRef.current?.focus();
                    }}
                    placeholder="0"
                    style={{
                      width: '100%',
                      padding: '3px 2px',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      border: '1px solid #2563eb'
                    }}
                  />
                </td>

                {/* DISC (Rp) */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputDiscRp)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe'
                    }}
                  />
                </td>

                {/* JUMLAH HARGA JADI */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputHrgJadiBeli)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textAlign: 'right',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4',
                      color: '#0f766e'
                    }}
                  />
                </td>

                {/* PPN MASUKAN */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputPpnBeli)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4'
                    }}
                  />
                </td>

                {/* KONV RUBAH */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1' }}>
                  <input
                    ref={inputKonvRef}
                    type="number"
                    value={inputKonversi}
                    onChange={(e) => handleKonvChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSimpanItem();
                    }}
                    placeholder="1"
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      border: '1px solid #ea580c'
                    }}
                  />
                </td>

                {/* SATUAN JUAL */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1' }}>
                  <input
                    type="text"
                    readOnly
                    value={inputSatuanJual}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textAlign: 'center',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0'
                    }}
                  />
                </td>

                {/* HARGA NETT */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputHargaNett)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0'
                    }}
                  />
                </td>

                {/* PPN [Rp] */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputPpnNett)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0'
                    }}
                  />
                </td>

                {/* HARGA DASAR */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                  <input
                    type="text"
                    readOnly
                    value={fmtNum(inputHargaDasar)}
                    style={{
                      width: '100%',
                      padding: '3px 4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textAlign: 'right',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#1e40af'
                    }}
                  />
                </td>

                {/* CEK FISIK / TOMBOL SIMPAN BARIS */}
                <td style={{ padding: '3px 4px', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleSimpanItem}
                    disabled={!inputKdObat}
                    className="btn btn-sm btn-primary"
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '0.70rem',
                      fontWeight: 700
                    }}
                    title="Tambahkan ke Draft"
                  >
                    <i className="bi bi-plus-lg me-1" /> Tambah
                  </button>
                </td>

                {/* BATAL / BERSIH BARIS */}
                <td style={{ padding: '3px 2px', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleBersihInputRow}
                    className="btn btn-sm btn-outline-secondary"
                    style={{
                      padding: '2px 6px',
                      fontSize: '0.70rem'
                    }}
                    title="Bersihkan Input"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            </thead>

            {/* TABEL DATA BODY */}
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                    <i className="bi bi-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: 6 }} />
                    Draft Tanda Terima Barang masih kosong.<br />
                    Pilih Nomor SP dan masukkan obat yang diterima dari faktur pemasok.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const isSelected = selectedRowIndex === idx;
                  const isCekFisikV = it.cekfisik === 'V';
                  const rowBg = isSelected
                    ? '#bfdbfe'
                    : isCekFisikV
                    ? '#f0fdf4'
                    : idx % 2 === 1
                    ? '#f8fafc'
                    : '#ffffff';

                  return (
                    <tr
                      key={idx}
                      onClick={() => handleRowClick(it, idx)}
                      style={{
                        background: rowBg,
                        borderBottom: '1px solid #e2e8f0',
                        cursor: 'pointer',
                        transition: 'background 0.1s'
                      }}
                    >
                      <td style={{ padding: '5px 8px', fontWeight: 700, color: '#0f172a' }}>
                        {it.nm_obat}
                      </td>
                      <td style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 700 }}>
                        {fmtNum(it.qtybeli)}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'center', color: '#64748b' }}>
                        {it.nmsatuanbeli || '-'}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        {fmtNum(it.totpricelist)}
                      </td>
                      <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                        {it.disc || 0}%
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        {fmtNum(it.discrp)}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700, color: '#0f766e' }}>
                        {fmtNum(it.hrgjadibeli)}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        {fmtNum(it.ppnbeli)}
                      </td>
                      <td style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600 }}>
                        {it.konversi || 1}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'center', color: '#64748b' }}>
                        {it.nmsatuanjual || '-'}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        {fmtNum(it.harga)}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        {fmtNum(it.ppn)}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>
                        {fmtNum(it.harsat)}
                      </td>

                      {/* TOMBOL TOGGLE CEK FISIK */}
                      <td style={{ padding: '3px 4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => handleOpenCekFisik(it)}
                          style={{
                            background: isCekFisikV ? '#15803d' : '#eab308',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 3,
                            padding: '2px 6px',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3
                          }}
                          title="Klik untuk konfirmasi Cek Fisik"
                        >
                          <i className={`bi ${isCekFisikV ? 'bi-check-circle-fill' : 'bi-question-circle'}`} />
                          {isCekFisikV ? 'SESUAI' : 'CEK FISIK'}
                        </button>
                      </td>

                      {/* TOMBOL HAPUS BARIS */}
                      <td style={{ padding: '3px 2px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          disabled={isApproved}
                          onClick={() => handleHapusItem(it.kd_obat)}
                          className="btn btn-sm btn-outline-danger"
                          style={{ padding: '1px 5px', fontSize: '0.68rem' }}
                          title="Hapus dari draft"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 5. FORMULA & SUBTOTAL BAR */}
        <div style={{
          background: '#e2e8f0',
          borderTop: '2px solid #cbd5e1',
          padding: '4px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
          fontSize: '0.74rem'
        }}>
          {/* Subtotal Tot Price List */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.68rem', color: '#475569' }}>Tot. Price:</span>
            <input
              type="text"
              readOnly
              value={fmtNum(totals.totpricelist)}
              style={{ width: 85, background: '#fff', border: '1px solid #94a3b8', padding: '1px 4px', textAlign: 'right', fontWeight: 700 }}
            />
          </div>

          <span style={{ fontWeight: 800, color: '#0369a1' }}>-</span>

          {/* Subtotal Disc */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.68rem', color: '#475569' }}>Disc Rp:</span>
            <input
              type="text"
              readOnly
              value={fmtNum(totals.subtotaldiscrp)}
              style={{ width: 75, background: '#fff', border: '1px solid #94a3b8', padding: '1px 4px', textAlign: 'right', fontWeight: 700 }}
            />
          </div>

          <span style={{ fontWeight: 800, color: '#0369a1' }}>=</span>

          {/* Subtotal Harga Jadi */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.68rem', color: '#475569' }}>Hrg Jadi:</span>
            <input
              type="text"
              readOnly
              value={fmtNum(totals.subtotalharga)}
              style={{ width: 85, background: '#fff', border: '1px solid #0f766e', padding: '1px 4px', textAlign: 'right', fontWeight: 700, color: '#0f766e' }}
            />
          </div>

          <span style={{ fontWeight: 800, color: '#0369a1' }}>+</span>

          {/* PPN Masukan */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.68rem', color: '#475569' }}>PPN:</span>
            <input
              type="text"
              readOnly
              value={fmtNum(totals.totppnbeli)}
              style={{ width: 75, background: '#fff', border: '1px solid #94a3b8', padding: '1px 4px', textAlign: 'right', fontWeight: 700 }}
            />
          </div>
        </div>

        {/* 6. RED WARNING BOX (PERSIS GAMBAS SCREENSHOT) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          background: '#f1f5f9',
          borderTop: '2px solid #94a3b8',
          padding: '6px 10px',
          alignItems: 'center',
          gap: 12
        }}>
          {/* Banner Merah */}
          <div style={{
            background: '#881337',
            color: '#ffffff',
            padding: '6px 14px',
            borderRadius: 3,
            fontSize: '0.75rem',
            lineHeight: 1.3,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontWeight: 800 }}>
              Total Draft Tanda Terima ini "HARUS" sama dengan total Faktur Pemasok.
            </div>
            <div style={{ fontSize: '0.70rem', color: '#fecdd3' }}>
              Jika total TIDAK SAMA dengan total Faktur Pemasok, maka RUBAH posisi "DISCOUNT".
            </div>
          </div>

          {/* SUMMARY CARDS KANAN: DISC TUNAI, PPN MASUKAN, MATERAI, TOTAL PEMBELIAN */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>

            {/* DISC TUNAI (Merah) */}
            <div style={{ border: '1px solid #9f1239', borderRadius: 3, overflow: 'hidden', width: 90, background: '#fff' }}>
              <div style={{ background: '#9f1239', color: '#fff', fontSize: '0.64rem', fontWeight: 800, textAlign: 'center', padding: '2px 2px', lineHeight: 1.1 }}>
                DISC.<br />TUNAI
              </div>
              <input
                type="number"
                value={discTunai}
                onChange={(e) => setDiscTunai(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  border: 'none',
                  padding: '4px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  textAlign: 'right',
                  outline: 'none'
                }}
              />
            </div>

            {/* PPN MASUKAN (Biru) */}
            <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', width: 95, background: '#fff' }}>
              <div style={{ background: '#0284c7', color: '#fff', fontSize: '0.64rem', fontWeight: 800, textAlign: 'center', padding: '2px 2px', lineHeight: 1.1 }}>
                PPN<br />MASUKAN
              </div>
              <input
                type="text"
                readOnly
                value={fmtNum(totPpnBeli)}
                style={{
                  width: '100%',
                  border: 'none',
                  padding: '4px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  textAlign: 'right',
                  outline: 'none',
                  background: '#f0f9ff'
                }}
              />
            </div>

            {/* MATERAI (Biru) */}
            <div style={{ border: '1px solid #0369a1', borderRadius: 3, overflow: 'hidden', width: 85, background: '#fff' }}>
              <div style={{ background: '#0284c7', color: '#fff', fontSize: '0.64rem', fontWeight: 800, textAlign: 'center', padding: '2px 2px', lineHeight: 1.1 }}>
                MATERAI<br />&nbsp;
              </div>
              <input
                type="number"
                value={materai}
                onChange={(e) => setMaterai(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  border: 'none',
                  padding: '4px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  textAlign: 'right',
                  outline: 'none'
                }}
              />
            </div>

            {/* TOTAL PEMBELIAN (Biru Gelap) */}
            <div style={{ border: '2px solid #0284c7', borderRadius: 3, overflow: 'hidden', width: 140, background: '#0f172a' }}>
              <div style={{ background: '#0369a1', color: '#fff', fontSize: '0.66rem', fontWeight: 800, textAlign: 'center', padding: '2px 2px' }}>
                TOTAL PEMBELIAN
              </div>
              <div style={{
                color: '#38bdf8',
                padding: '3px 6px',
                fontSize: '0.96rem',
                fontWeight: 900,
                textAlign: 'right',
                letterSpacing: '0.5px'
              }}>
                Rp {fmtNum(grandTotal)}
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* 7. BOTTOM ACTION CONTROLS FOOTER (PERSIS GAMBAS SCREENSHOT) */}
      <div style={{
        background: '#cbd5e1',
        borderTop: '2px solid #94a3b8',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* KIRI: CETAK ULANG TTB (JIKA ADA NOMOR BUKTI) */}
        <div style={{ display: 'flex', gap: 8 }}>
          {noBukti && (
            <button
              type="button"
              onClick={() => handleCetakPdf(noBukti)}
              className="btn btn-sm btn-success d-flex align-items-center gap-1.5"
              style={{
                padding: '5px 14px',
                fontSize: '0.80rem',
                fontWeight: 700
              }}
              title="Cetak PDF TTB Resmi"
            >
              <i className="bi bi-printer-fill" />
              <span>CETAK TTB</span>
            </button>
          )}
        </div>

        {/* KANAN: PROSES DRAFT, HAPUS, PROSES APPROVAL, KELUAR */}
        <div style={{ display: 'flex', gap: 8 }}>
          {/* TOMBOL PROSES DRAFT */}
          <button
            type="button"
            disabled={isLoading || !noOph}
            onClick={handleSimpanDraftOnly}
            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1.5"
            style={{
              padding: '5px 14px',
              fontSize: '0.80rem',
              fontWeight: 700,
              background: '#fff'
            }}
            title="Simpan perhitungan ke draft OPH"
          >
            <i className="bi bi-arrow-repeat text-primary" />
            <span>PROSES DRAFT</span>
          </button>
          {/* TOMBOL HAPUS */}
          <button
            type="button"
            disabled={isLoading || isApproved || items.length === 0}
            onClick={() => handleHapusItem()}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1.5"
            style={{
              padding: '5px 14px',
              fontSize: '0.80rem',
              fontWeight: 700,
              background: '#fff'
            }}
            title="Hapus obat yang dipilih"
          >
            <i className="bi bi-trash" />
            <span>HAPUS</span>
          </button>

          {/* TOMBOL PROSES APPROVAL TTB */}
          <button
            type="button"
            disabled={isLoading || isApproved || items.length === 0}
            onClick={handleOpenConfirmApproval}
            className="btn btn-sm btn-primary d-flex align-items-center gap-1.5"
            style={{
              padding: '5px 18px',
              fontSize: '0.82rem',
              fontWeight: 800,
              background: '#0284c7',
              borderColor: '#0369a1'
            }}
            title="Approval Final TTB, Penomoran, dan Mutasi Stok"
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <i className="bi bi-check2-circle" />
            )}
            <span>PROSES (TTB)</span>
          </button>

          {/* TOMBOL KELUAR */}
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="btn btn-sm btn-danger d-flex align-items-center gap-1.5"
            style={{
              padding: '5px 16px',
              fontSize: '0.82rem',
              fontWeight: 800
            }}
            title="Keluar ke Halaman Utama (Home)"
          >
            <i className="bi bi-x-circle-fill" />
            <span>KELUAR</span>
          </button>
        </div>
      </div>

      {/* ==========================================
          MODAL 1: DAFTAR SURAT PESANAN (LOOKUP SP)
      ========================================== */}
      <Modal show={showModalSp} onHide={() => setShowModalSp(false)} size="lg" centered>
        <Modal.Header style={{ background: '#0284c7', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 800 }}>
            <i className="bi bi-file-earmark-text me-2" />
            Pilih Surat Pesanan (SP) Apotek
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowModalSp(false)}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer' }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <input
              type="text"
              value={searchSpKeyword}
              onChange={(e) => setSearchSpKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleOpenModalSp();
              }}
              placeholder="Cari berdasarkan No SP, Nama Pemasok, atau Kode..."
              className="form-control form-control-sm"
              style={{ fontSize: '0.80rem' }}
            />
            <Button size="sm" variant="primary" onClick={handleOpenModalSp}>
              <i className="bi bi-search me-1" /> Cari
            </Button>
          </div>

          <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <Table hover size="sm" style={{ fontSize: '0.74rem', margin: 0 }}>
              <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  <th>No. Surat Pesanan</th>
                  <th>Tanggal</th>
                  <th>Pemasok</th>
                  <th>Cara Bayar</th>
                  <th style={{ textAlign: 'right' }}>Total (Rp)</th>
                  <th style={{ textAlign: 'center' }}>Pilih</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingSpList ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                      <Spinner animation="border" size="sm" /> Memuat data SP...
                    </td>
                  </tr>
                ) : spHistoryList.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>
                      Tidak ada Surat Pesanan ditemukan.
                    </td>
                  </tr>
                ) : (
                  spHistoryList.map((sp, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 700, color: '#0369a1' }}>{sp.no_sp}</td>
                      <td>{sp.tanggal}</td>
                      <td style={{ fontWeight: 600 }}>{sp.nm_suplier || sp.kd_suplier || '-'}</td>
                      <td>{sp.cara_byr || 'Tempo'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNum(sp.total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Button size="sm" variant="outline-primary" style={{ padding: '1px 8px', fontSize: '0.70rem' }} onClick={() => handleSelectSp(sp)}>
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ==========================================
          MODAL 2: DAFTAR OBAT DARI SP YANG DIPILIH
      ========================================== */}
      <Modal show={showModalObat} onHide={() => setShowModalObat(false)} size="lg" centered>
        <Modal.Header style={{ background: '#0f766e', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 800 }}>
            <i className="bi bi-capsule me-2" />
            Daftar Obat dari SP: {noPo}
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowModalObat(false)}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer' }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: 12 }}>
          <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 8 }}>
            Pilih obat yang tertera pada surat pesanan untuk dimasukkan ke penerimaan barang:
          </p>
          <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <Table hover size="sm" style={{ fontSize: '0.74rem', margin: 0 }}>
              <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  <th>Kode</th>
                  <th>Nama Obat</th>
                  <th>Satuan Beli</th>
                  <th style={{ textAlign: 'center' }}>Qty SP</th>
                  <th style={{ textAlign: 'right' }}>Price List</th>
                  <th style={{ textAlign: 'center' }}>Disc %</th>
                  <th style={{ textAlign: 'center' }}>Konversi</th>
                  <th>Satuan Jual</th>
                  <th style={{ textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {spItemsList.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>
                      Tidak ada data obat di SP ini.
                    </td>
                  </tr>
                ) : (
                  spItemsList.map((ob, idx) => (
                    <tr key={idx}>
                      <td style={{ fontFamily: 'monospace' }}>{ob.kd_obat}</td>
                      <td style={{ fontWeight: 700 }}>{ob.nm_obat}</td>
                      <td>{ob.satuanbeli || '-'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#0284c7' }}>{ob.qty_sp}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(ob.pricelist)}</td>
                      <td style={{ textAlign: 'center' }}>{ob.disc || 0}%</td>
                      <td style={{ textAlign: 'center' }}>{ob.konversi || 1}</td>
                      <td>{ob.satuanjual || '-'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Button size="sm" variant="success" style={{ padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700 }} onClick={() => handleSelectObatFromSp(ob)}>
                          <i className="bi bi-check-lg me-1" /> Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ==========================================
          MODAL 3: KONFIRMASI CEK FISIK BARANG
      ========================================== */}
      <Modal show={showModalCekFisik} onHide={() => setShowModalCekFisik(false)} centered>
        <Modal.Header style={{ background: '#0284c7', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.90rem', fontWeight: 800 }}>
            <i className="bi bi-patch-check me-2" />
            Pemeriksaan Fisik Barang Kiriman
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px 20px' }}>
          <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
            {targetCekFisikItem?.nm_obat}
          </h6>
          <p style={{ fontSize: '0.82rem', color: '#475569', margin: 0 }}>
            Apakah data fisik barang kiriman dari gudang / PBF sudah sesuai dengan faktur dan kondisi fisik baik (tidak rusak / ED aman)?
          </p>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc' }}>
          <Button variant="outline-danger" size="sm" onClick={() => handleExecuteCekFisik(false)}>
            <i className="bi bi-x-lg me-1" /> Tidak Sesuai (X)
          </Button>
          <Button variant="success" size="sm" style={{ fontWeight: 700 }} onClick={() => handleExecuteCekFisik(true)}>
            <i className="bi bi-check-lg me-1" /> Sesuai Fisik (V)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ==========================================
          MODAL 4: KONFIRMASI APPROVAL TTB
      ========================================== */}
      <Modal show={showModalConfirmApp} onHide={() => setShowModalConfirmApp(false)} centered>
        <Modal.Header style={{ background: '#0369a1', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.90rem', fontWeight: 800 }}>
            <i className="bi bi-shield-check me-2" />
            Konfirmasi Approval Tanda Terima Barang
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Nomor Faktur Pemasok:</div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{noFaktur}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Total Nilai Pembelian:</div>
            <div style={{ fontWeight: 900, fontSize: '1.1rem', color: '#0284c7' }}>Rp {fmtNum(grandTotal)}</div>
          </div>
          <div style={{ padding: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, fontSize: '0.78rem', color: '#991b1b' }}>
            <strong>Penting:</strong> Periksa apakah nilai total tanda terima barang sudah sama dengan nilai total faktur pemasok.
            Setelah diapprove, nomor TTB resmi akan diterbitkan, saldo stok obat pada kartu SAPOTEK akan bertambah, dan tagihan akan dicatat ke Buku Hutang Apotek.
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModalConfirmApp(false)}>
            Batal
          </Button>
          <Button variant="primary" size="sm" style={{ fontWeight: 800, background: '#0284c7', borderColor: '#0369a1' }} onClick={handleExecuteApproval}>
            <i className="bi bi-check-lg me-1" /> Ya, Setujui & Terbitkan TTB
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ==========================================
          MODAL 5: PDF VIEWER MODAL
      ========================================== */}
      <Modal show={showModalPdf} onHide={() => setShowModalPdf(false)} size="xl" centered>
        <Modal.Header style={{ background: '#0f172a', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.90rem', fontWeight: 800 }}>
            <i className="bi bi-file-earmark-pdf-fill me-2 text-danger" />
            {pdfTitle || 'Dokumen Tanda Terima Barang'}
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowModalPdf(false)}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer' }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '78vh', background: '#334155' }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="PDF TTB"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 14px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
            RS Mary Cileungsi Hijau &bull; Tanda Terima Barang Apotek
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {pdfUrl && (
              <a
                href={pdfUrl}
                download="Tanda_Terima_Barang_Apotek.pdf"
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: '0.76rem', fontWeight: 700 }}
              >
                <i className="bi bi-download me-1" /> Unduh PDF
              </a>
            )}
            <Button size="sm" variant="secondary" onClick={() => setShowModalPdf(false)}>
              Tutup
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ==========================================
          MODAL 6: RIWAYAT / CETAK ULANG TTB
      ========================================== */}
      <Modal show={showModalHistory} onHide={() => setShowModalHistory(false)} size="xl" centered>
        <Modal.Header style={{ background: '#0284c7', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 800 }}>
            <i className="bi bi-clock-history me-2" />
            Riwayat Tanda Terima Barang Selesai (Cetak Ulang)
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowModalHistory(false)}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer' }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <input
              type="text"
              value={searchTtbKeyword}
              onChange={(e) => setSearchTtbKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleOpenModalHistory();
              }}
              placeholder="Cari berdasarkan No TTB, Faktur, SP, atau Pemasok..."
              className="form-control form-control-sm"
              style={{ fontSize: '0.80rem' }}
            />
            <Button size="sm" variant="primary" onClick={handleOpenModalHistory}>
              <i className="bi bi-search me-1" /> Cari
            </Button>
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <Table hover size="sm" style={{ fontSize: '0.74rem', margin: 0 }}>
              <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  <th>No. TTB</th>
                  <th>No. Faktur</th>
                  <th>No. SP</th>
                  <th>Pemasok</th>
                  <th>Tanggal</th>
                  <th style={{ textAlign: 'right' }}>Total (Rp)</th>
                  <th style={{ textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {ttbHistoryList.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>
                      Tidak ada riwayat TTB ditemukan.
                    </td>
                  </tr>
                ) : (
                  ttbHistoryList.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 800, color: '#0369a1' }}>{row.no_bukti}</td>
                      <td style={{ fontWeight: 700 }}>{row.no_faktur}</td>
                      <td>{row.no_po || '-'}</td>
                      <td>{row.nm_suplier || '-'}</td>
                      <td>{row.tanggal || row.tgl_faktur}</td>
                      <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmtNum(row.total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          style={{ padding: '1px 8px', fontSize: '0.70rem', fontWeight: 700 }}
                          onClick={() => {
                            setShowModalHistory(false);
                            handleCetakPdf(row.no_bukti, row.no_oph);
                          }}
                        >
                          <i className="bi bi-printer me-1" /> Cetak PDF
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ==========================================
          MODAL 7: REDESIGN UI/UX LAPORAN PEMBELIAN & RETUR
      ========================================== */}
      <Modal
        show={showModalLaporan}
        onHide={() => !isProcessingLap && setShowModalLaporan(false)}
        centered
        dialogClassName="modal-laporan-redesign"
      >
        <style>{`
          .modal-laporan-redesign {
            max-width: 580px !important;
            width: 95vw !important;
          }
          .modal-laporan-redesign .modal-content {
            border: none;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
          }
        `}</style>

        {/* HEADER MODAL REDESIGN */}
        <Modal.Header
          closeButton
          style={{
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            padding: '14px 20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: lapJenis === 'retur' ? '#fee2e2' : '#e0f2fe',
              color: lapJenis === 'retur' ? '#dc2626' : '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.3rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <i className={`bi ${lapJenis === 'retur' ? 'bi-arrow-return-left' : 'bi-file-earmark-bar-graph-fill'}`} />
            </div>
            <div>
              <h6 style={{ margin: 0, fontWeight: 800, color: '#0f172a', fontSize: '1rem', letterSpacing: '-0.2px' }}>
                {lapJenis === 'retur' ? 'Laporan Retur Pembelian' : 'Laporan Pembelian Apotek'}
              </h6>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                Instalasi Farmasi &bull; RS Mary Cileungsi Hijau
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* BODY MODAL REDESIGN */}
        <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, background: '#ffffff' }}>
          {/* 1. SEGMENTED CONTROL: JENIS LAPORAN (COMPACT & MODERN) */}
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Jenis Laporan
            </label>
            <div style={{
              display: 'flex',
              background: '#f1f5f9',
              padding: 4,
              borderRadius: 8,
              gap: 6
            }}>
              <button
                type="button"
                onClick={() => setLapJenis('pembelian')}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  fontSize: '0.80rem',
                  fontWeight: 800,
                  border: 'none',
                  borderRadius: 6,
                  background: lapJenis === 'pembelian' ? '#0284c7' : 'transparent',
                  color: lapJenis === 'pembelian' ? '#ffffff' : '#475569',
                  boxShadow: lapJenis === 'pembelian' ? '0 2px 5px rgba(2,132,199,0.3)' : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer'
                }}
              >
                <i className="bi bi-cart-check-fill" />
                <span>1. Laporan Pembelian</span>
              </button>

              <button
                type="button"
                onClick={() => setLapJenis('retur')}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  fontSize: '0.80rem',
                  fontWeight: 800,
                  border: 'none',
                  borderRadius: 6,
                  background: lapJenis === 'retur' ? '#dc2626' : 'transparent',
                  color: lapJenis === 'retur' ? '#ffffff' : '#475569',
                  boxShadow: lapJenis === 'retur' ? '0 2px 5px rgba(220,38,38,0.3)' : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer'
                }}
              >
                <i className="bi bi-arrow-return-left" />
                <span>2. Laporan Retur Pembelian</span>
              </button>
            </div>
          </div>

          {/* 2. LOKASI GUDANG */}
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Lokasi Gudang / Apotek
            </label>
            <select
              value={lapKdGudang}
              onChange={(e) => setLapKdGudang(e.target.value)}
              className="form-select form-select-sm"
              style={{
                padding: '8px 12px',
                fontSize: '0.82rem',
                fontWeight: 700,
                borderRadius: 6,
                borderColor: '#cbd5e1',
                color: '#0f172a',
                background: '#f8fafc'
              }}
            >
              {gudangList.map((g, idx) => (
                <option key={idx} value={g.kd_gudang}>
                  {g.kd_gudang} &bull; {g.nm_gudang}
                </option>
              ))}
            </select>
          </div>

          {/* 3. PERIODE TRANSAKSI DENGAN DATE PICKER NATIVE & PRESET */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Periode Transaksi
              </label>
              {/* Quick Presets */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  onClick={setPresetToday}
                  className="btn btn-sm btn-outline-secondary"
                  style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}
                >
                  Hari Ini
                </button>
                <button
                  type="button"
                  onClick={setPresetThisMonth}
                  className="btn btn-sm btn-outline-secondary"
                  style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}
                >
                  Bulan Ini
                </button>
                <button
                  type="button"
                  onClick={setPresetLastMonth}
                  className="btn btn-sm btn-outline-secondary"
                  style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}
                >
                  Bulan Lalu
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <input
                  type="date"
                  value={dmyToYmd(lapPeriode1)}
                  onChange={(e) => setLapPeriode1(ymdToDmy(e.target.value))}
                  className="form-control form-control-sm"
                  style={{ fontSize: '0.82rem', fontWeight: 700, padding: '7px 10px', borderRadius: 6, borderColor: '#cbd5e1' }}
                />
              </div>
              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#94a3b8' }}>s/d</span>
              <div style={{ flex: 1 }}>
                <input
                  type="date"
                  value={dmyToYmd(lapPeriode2)}
                  onChange={(e) => setLapPeriode2(ymdToDmy(e.target.value))}
                  className="form-control form-control-sm"
                  style={{ fontSize: '0.82rem', fontWeight: 700, padding: '7px 10px', borderRadius: 6, borderColor: '#cbd5e1' }}
                />
              </div>
            </div>
          </div>

          {/* 4. FORMAT DOKUMEN LAPORAN */}
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Format Dokumen Laporan
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <div
                onClick={() => setLapFormat('rekap')}
                style={{
                  border: lapFormat === 'rekap' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                  background: lapFormat === 'rekap' ? '#f0f9ff' : '#ffffff',
                  borderRadius: 8,
                  padding: '10px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <i className={`bi bi-file-earmark-spreadsheet ${lapFormat === 'rekap' ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }} />
                <div style={{ fontWeight: 800, fontSize: '0.76rem', marginTop: 4, color: lapFormat === 'rekap' ? '#0369a1' : '#1e293b' }}>
                  Rekapitulasi
                </div>
                <div style={{ fontSize: '0.66rem', color: '#64748b' }}>
                  Per Bukti / Faktur
                </div>
              </div>

              <div
                onClick={() => setLapFormat('rinci')}
                style={{
                  border: lapFormat === 'rinci' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                  background: lapFormat === 'rinci' ? '#f0f9ff' : '#ffffff',
                  borderRadius: 8,
                  padding: '10px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <i className={`bi bi-list-columns-reverse ${lapFormat === 'rinci' ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }} />
                <div style={{ fontWeight: 800, fontSize: '0.76rem', marginTop: 4, color: lapFormat === 'rinci' ? '#0369a1' : '#1e293b' }}>
                  Rincian Obat
                </div>
                <div style={{ fontSize: '0.66rem', color: '#64748b' }}>
                  Per Nama Obat
                </div>
              </div>

              <div
                onClick={() => setLapFormat('both')}
                style={{
                  border: lapFormat === 'both' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                  background: lapFormat === 'both' ? '#f0f9ff' : '#ffffff',
                  borderRadius: 8,
                  padding: '10px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <i className={`bi bi-files ${lapFormat === 'both' ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }} />
                <div style={{ fontWeight: 800, fontSize: '0.76rem', marginTop: 4, color: lapFormat === 'both' ? '#0369a1' : '#1e293b' }}>
                  Kedua Laporan
                </div>
                <div style={{ fontSize: '0.66rem', color: '#64748b' }}>
                  Rekap & Rinci
                </div>
              </div>
            </div>
          </div>

          {/* 5. LIVE STATS / RINGKASAN DATA TRANSAKSI */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {isLoadingLapSummary ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.76rem', color: '#0284c7', fontWeight: 600 }}>
                <Spinner animation="border" size="sm" />
                <span>Memuat data transaksi...</span>
              </div>
            ) : lapSummary && lapSummary.count > 0 ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '0.70rem', color: '#64748b', display: 'block' }}>Data Ditemukan</span>
                  <span style={{ fontSize: '0.84rem', fontWeight: 800, color: '#0f172a' }}>
                    {lapSummary.count} Dokumen
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.70rem', color: '#64748b', display: 'block' }}>Total Kuantitas</span>
                  <span style={{ fontSize: '0.84rem', fontWeight: 800, color: '#0284c7' }}>
                    {fmtNum(lapSummary.total_qty)} Qty
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.70rem', color: '#64748b', display: 'block' }}>Total Nilai</span>
                  <span style={{ fontSize: '0.88rem', fontWeight: 900, color: '#059669' }}>
                    Rp {fmtNum(lapSummary.total_nilai)}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', fontStyle: 'italic' }}>
                <i className="bi bi-exclamation-circle me-1" />
                Tidak ada data transaksi pada periode & gudang terpilih.
              </div>
            )}
          </div>

          {/* 6. PROGRESS BAR (pbbar) */}
          {(isProcessingLap || lapProgress > 0) && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: 8,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.74rem' }}>
                <span style={{ fontWeight: 700, color: lapProgress === 100 ? '#16a34a' : '#0369a1' }}>
                  {isProcessingLap && <Spinner animation="border" size="sm" className="me-2" style={{ width: 12, height: 12, borderWidth: 2 }} />}
                  {lapStatusMsg || 'Menyusun dokumen...'}
                </span>
                <span style={{ fontWeight: 900, color: '#0f172a' }}>{lapProgress}%</span>
              </div>
              <div style={{
                height: 8,
                background: '#e2e8f0',
                borderRadius: 9999,
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${lapProgress}%`,
                  background: lapProgress === 100 ? '#22c55e' : 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                  transition: 'width 0.25s ease'
                }} />
              </div>
            </div>
          )}
        </Modal.Body>

        {/* FOOTER MODAL REDESIGN */}
        <Modal.Footer style={{
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
            <i className="bi bi-file-earmark-pdf text-danger me-1" />
            Format PDF Resmi A4 Landscape
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isProcessingLap}
              onClick={() => setShowModalLaporan(false)}
              style={{ fontWeight: 700, fontSize: '0.78rem', padding: '6px 14px', borderRadius: 6 }}
            >
              Tutup
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isProcessingLap}
              onClick={handleProsesLaporan}
              style={{
                fontWeight: 800,
                fontSize: '0.80rem',
                padding: '6px 18px',
                borderRadius: 6,
                background: '#0284c7',
                borderColor: '#0284c7',
                boxShadow: '0 2px 4px rgba(2,132,199,0.25)'
              }}
            >
              {isProcessingLap ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1.5" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-printer-fill me-1.5" />
                  <span>Proses & Cetak</span>
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default TerimaBarangApotek;
